import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { useActiveOrg } from '../activeOrg';

const EMAIL_PATTERN = /.+@.+\..+/;

export interface PermissionRoleOption {
  id: string;
  name: string;
  system_key: string | null;
}

export interface FunctionRoleOption {
  id: string;
  key: string | null;
  label: string;
}

export type InviteFormStatus =
  | 'idle'
  | 'loading-options'
  | 'options-error'
  | 'ready'
  | 'submitting'
  | 'error'
  | 'success';

/**
 * Inline per-row edit state (turn 2c.1b). `editing` is the steady state once
 * the row is in edit mode; `saving` is in-flight; `error` keeps the editor
 * open with `editError` populated; `options-error` covers the rare case where
 * the per-row Edit was clicked before the invite-form had a chance to cache
 * the dropdown options.
 */
export type EditRoleStatus =
  | 'idle'
  | 'loading-options'
  | 'options-error'
  | 'editing'
  | 'saving'
  | 'error';

/** Promote-to-Owner confirm-Modal state (turn 2c.1c.c). `submitting` is the
 *  `transfer_ownership` RPC in flight; `error` keeps the Modal open with the
 *  inline error visible. */
export type PromoteConfirmStatus = 'idle' | 'submitting' | 'error';

/** Header `Transfer ownership` Modal state (turn 2c.1c.d). Shares the
 *  `transfer_ownership` RPC with the promote-to-Owner flow but enters via the
 *  header button with an inline picker rather than the per-row inline edit. */
export type TransferModalStatus = 'idle' | 'submitting' | 'error';

/** Per-row Remove member confirm-Modal state (turn 2c.2.b). `submitting` is
 *  the `remove_member` RPC in flight; `error` keeps the Modal open with the
 *  inline error visible. */
export type RemoveStatus = 'idle' | 'submitting' | 'error';

/** Per-row Resend state surfaced to the page (turn 2c.3). `busy` is the
 *  resend_invitation RPC in flight; `cooldownSeconds` is the remaining
 *  client-side cooldown (≥ 30s after a success; matches `seconds_remaining`
 *  parsed off the server P0001 detail when the cooldown is rejected); `error`
 *  is rendered inline under the row when set. */
export interface ResendRowState {
  busy: boolean;
  cooldownSeconds: number;
  error: string | null;
}

export interface TransferTargetOption {
  /** `user_id` of the target Active member. Used as the Select option value. */
  userId: string;
  /** Display label: `<display_name> @<handle>#<discriminator>` (handle/disc
   *  fall back when missing). */
  label: string;
  /** Display name for the post-transfer toast copy. */
  displayName: string;
}

export type MemberKind = 'active' | 'pending' | 'expired';

export interface MemberRow {
  kind: MemberKind;
  user_id: string | null;
  invitation_id: string | null;
  display_name: string | null;
  username: string | null;
  discriminator: number | null;
  /** Null when the caller is not Owner/Admin (RPC nulls server-side). */
  email: string | null;
  permission_role_system_key: string | null;
  permission_role_name: string | null;
  function_role_label: string | null;
  last_active_at: string | null;
  invited_at: string | null;
  expires_at: string | null;
  is_self: boolean;
}

export type MembersStatus = 'loading' | 'ready' | 'unauth' | 'empty' | 'error';

export type ViewerPermissionSystemKey = 'owner' | 'admin' | 'member' | null;

export interface MemberCapabilities {
  /** Viewer can invite a new member (gates the header `Invite member` button +
   *  per-row `Resend`/`Revoke`/`Re-invite` on Pending/Expired rows). Owner +
   *  Admin by default; custom permission groups land in `settings-permission-groups`. */
  canInvite: boolean;
  /** Viewer can transfer ownership (gates the header `Transfer ownership`
   *  button). Owner-only. */
  canTransfer: boolean;
  /** Viewer can edit other members' permission_role / function_role (gates the
   *  per-row `Edit` button). Owner + Admin by default. Per-row blocks (own row,
   *  Owner row from an Admin viewer, Owner row for direct edit) are resolved
   *  in `rowActions()` below. */
  canEditRole: boolean;
  /** Viewer can remove other members (gates the per-row `Remove` button).
   *  Owner + Admin by default. Per-row blocks resolved in `rowActions()`. */
  canRemove: boolean;
}

export interface RowActionVisibility {
  /** Show the row's `Edit` button. */
  edit: boolean;
  /** Show the row's `Remove` button. */
  remove: boolean;
  /** Show the row's `Resend` button (Pending only). */
  resend: boolean;
  /** Show the row's `Re-invite` button (Expired only). */
  reinvite: boolean;
  /** Show the row's `Revoke` button (Pending or Expired). */
  revoke: boolean;
}

export interface UseMembersVM {
  status: MembersStatus;
  /** Org id queried via list_org_members. Sourced from the shell-owned
   *  active-org context (`useActiveOrg`) so it tracks the org the user
   *  switched into, rather than re-deriving the oldest membership. */
  activeOrgId: string | null;
  activeOrgName: string | null;
  rows: ReadonlyArray<MemberRow>;
  /** True when at least one row carries an email (RPC populates email only
   *  for Owner/Admin callers — Members get nulls). Drives the email-column
   *  visibility on the page. */
  canSeeEmails: boolean;
  /** Counts derived from `rows` for the page meta line. */
  activeCount: number;
  pendingCount: number;
  expiredCount: number;
  /** Caller's permission role in the active org, sourced from the `is_self`
   *  row in `list_org_members`. Drives the capability gates below + the
   *  header / per-row controls in the view. Falls back to `null` when the
   *  caller is not represented in the RPC's active-rows section (e.g., the
   *  viewer was removed mid-session). */
  viewerPermissionSystemKey: ViewerPermissionSystemKey;
  /** Capability flags derived from `viewerPermissionSystemKey`. */
  capabilities: MemberCapabilities;
  /** Per-row action visibility resolver. Applies viewer-role capability +
   *  per-row exclusions per `../web-mock/features/members/prd.md` § "Per-row
   *  actions". The buttons themselves ship as disabled no-ops in turn 2a.2;
   *  wiring lands in 2b.* / 2c.*. */
  rowActions: (row: MemberRow) => RowActionVisibility;
  error: string | null;
  /** Invite-form state (turn 2b.3b). Submit lands in 2b.3c. */
  inviteFormOpen: boolean;
  inviteEmail: string;
  invitePermissionRoleId: string;
  inviteFunctionRoleId: string;
  inviteStatus: InviteFormStatus;
  inviteError: string | null;
  permissionRoleOptions: ReadonlyArray<PermissionRoleOption>;
  /** Permission-role options for the invite form — always excludes Owner. */
  invitePermissionRoleOptions: ReadonlyArray<PermissionRoleOption>;
  /** Permission-role options for the inline-edit row — includes Owner only
   *  when the viewer is Owner (per `viewerPermissionSystemKey === 'owner'`). */
  editPermissionRoleOptions: ReadonlyArray<PermissionRoleOption>;
  functionRoleOptions: ReadonlyArray<FunctionRoleOption>;
  /** Trimmed RFC-5322-ish validity of `inviteEmail`. */
  inviteEmailValid: boolean;
  /** True when the form is open, email is valid, and both roles are picked. */
  inviteFormCanSubmit: boolean;
  /** Email of the invitation just sent. Drives the success toast. Cleared via
   *  `clearInviteToast()`. */
  inviteSentTo: string | null;
  /** True when the RPC succeeded but the Edge Function delivery failed. Drives
   *  the fallback toast copy. */
  inviteEdgeFailed: boolean;
  /** Email of a pending row to scroll into view (set on `still_pending`
   *  rejection). The page reads this off the wrapper and scrolls the matching
   *  row into view, then clears it via `clearInviteScrollTarget`. */
  inviteScrollToEmail: string | null;
  openInviteForm: () => void;
  cancelInviteForm: () => void;
  setInviteEmail: (v: string) => void;
  setInvitePermissionRoleId: (v: string) => void;
  setInviteFunctionRoleId: (v: string) => void;
  submitInvite: () => Promise<void>;
  clearInviteToast: () => void;
  clearInviteScrollTarget: () => void;
  /** Per-row edit state (turn 2c.1b). The currently-editing row's `user_id`,
   *  or `null` when no row is in edit mode. */
  editingUserId: string | null;
  /** Working selection for the inline permission-role dropdown. Pre-populated
   *  with the row's current permission_role id (matched by `system_key`
   *  against the cached options). */
  editPermissionRoleId: string;
  /** Working selection for the inline function-role dropdown. Pre-populated
   *  with the row's current function_role id (matched by `label`). */
  editFunctionRoleId: string;
  editStatus: EditRoleStatus;
  editError: string | null;
  /** `user_id` of the row that was just saved — drives the post-save toast. */
  editSavedFor: string | null;
  startEditRole: (row: MemberRow) => void;
  cancelEditRole: () => void;
  setEditPermissionRoleId: (v: string) => void;
  setEditFunctionRoleId: (v: string) => void;
  saveRoleChange: () => Promise<void>;
  clearEditToast: () => void;
  /** Promote-to-Owner Modal state (turn 2c.1c.c). `targetUserId` is the row
   *  the Owner is about to promote; `null` means the Modal is closed. */
  promoteConfirmTargetUserId: string | null;
  promoteConfirmStatus: PromoteConfirmStatus;
  promoteConfirmError: string | null;
  /** `user_id` of the row that was just promoted — drives the post-promote
   *  toast. Independent of `editSavedFor` (different copy). */
  promoteSavedFor: string | null;
  /** Display name to render inside the confirm Modal copy. Captured at the
   *  moment `requestPromoteToOwner` is called so the modal copy is stable even
   *  if the underlying row mutates mid-confirm. */
  promoteConfirmName: string | null;
  requestPromoteToOwner: (targetUserId: string) => void;
  cancelPromoteToOwner: () => void;
  confirmPromoteToOwner: () => Promise<void>;
  clearPromoteToast: () => void;
  /** Header `Transfer ownership` Modal state (turn 2c.1c.d). `transferOpen`
   *  toggles the Modal; `transferTargetUserId` is the picker's value. */
  transferOpen: boolean;
  transferTargetUserId: string;
  transferStatus: TransferModalStatus;
  transferError: string | null;
  /** Display name of the user just promoted via the header Transfer flow —
   *  drives the post-transfer toast. Cleared by `clearTransferToast`. */
  transferSavedName: string | null;
  /** Active non-Owner non-self rows formatted as picker options for the
   *  header Transfer Modal. */
  transferTargetOptions: ReadonlyArray<TransferTargetOption>;
  openTransferModal: () => void;
  cancelTransferModal: () => void;
  setTransferTargetUserId: (v: string) => void;
  confirmTransfer: () => Promise<void>;
  clearTransferToast: () => void;
  /** Remove-member Modal state (turn 2c.2.b). `removeTargetUserId` is the row
   *  the viewer is about to remove; `null` means the Modal is closed. */
  removeTargetUserId: string | null;
  /** Display name captured at request-time so the Modal title + toast copy
   *  stay stable even if `rows` mutates mid-confirm. */
  removeTargetName: string | null;
  removeStatus: RemoveStatus;
  removeError: string | null;
  /** Display name of the just-removed member — drives the post-remove toast.
   *  Cleared by `clearRemoveToast`. */
  removeSavedName: string | null;
  requestRemove: (row: MemberRow) => void;
  cancelRemove: () => void;
  confirmRemove: () => Promise<void>;
  clearRemoveToast: () => void;
  /** Per-row Resend state (turn 2c.3). The page calls `resendStateFor(row)`
   *  to drive the per-row Resend button label / disabled state. */
  resendStateFor: (row: MemberRow) => ResendRowState;
  /** Email of the most-recently-resent invitation — drives the toast.
   *  Cleared by `clearResendToast`. */
  resendSavedEmail: string | null;
  /** True when the resend RPC succeeded but the Edge Function delivery
   *  failed — drives the fallback toast copy. */
  resendEdgeFailed: boolean;
  resendInvitation: (row: MemberRow) => Promise<void>;
  clearResendToast: () => void;
}

interface RawRow {
  kind?: string | null;
  user_id?: string | null;
  invitation_id?: string | null;
  display_name?: string | null;
  username?: string | null;
  discriminator?: number | null;
  email?: string | null;
  permission_role_system_key?: string | null;
  permission_role_name?: string | null;
  function_role_label?: string | null;
  last_active_at?: string | null;
  invited_at?: string | null;
  expires_at?: string | null;
  is_self?: boolean | null;
}

function normalizeKind(k: string | null | undefined): MemberKind | null {
  if (k === 'active' || k === 'pending' || k === 'expired') return k;
  return null;
}

/**
 * Optional hooks the page can pass to `useMembersVM` to coordinate with
 * out-of-VM state. Currently used by `/members` to drive a seat-usage refetch
 * from the page-level `useSeatUsage` after a successful invite (cluster 5G —
 * the seat-counter pill + Invite-button gate read from that hook and would
 * otherwise stay stale until the next visibilitychange/refetch).
 */
export interface MembersVMOptions {
  onInviteSuccess?: () => void;
}

/**
 * ViewModel for `/members` (turn 2a.1.1, read-only list view). Resolves the
 * caller's home org from `memberships`, then calls `list_org_members(p_org_id)`
 * — a `security definer` RPC that returns the unified Active + Pending +
 * Expired roster + nulls emails for non-Owner/Admin callers. Mutations
 * (invite, role-edit, remove, transfer) land in subsequent turns.
 */
export function useMembersVM(options: MembersVMOptions = {}): UseMembersVM {
  // Stash the latest callback in a ref so `submitInvite` doesn't need to be
  // re-memoized when the page passes a fresh closure each render — keeps the
  // dependency set on the existing useCallbacks small and stable.
  const onInviteSuccessRef = useRef(options.onInviteSuccess);
  onInviteSuccessRef.current = options.onInviteSuccess;
  const [status, setStatus] = useState<MembersStatus>('loading');
  // The active org is owned by the shell (see `useActiveOrg`). This VM is a
  // pure consumer: it scopes `list_org_members` + every mutation to whatever
  // org the shell says is active and re-loads when the user switches. It must
  // NOT re-derive its own "oldest membership" org — that was the cross-org leak.
  const { activeOrgId, activeOrgName } = useActiveOrg();
  const [rows, setRows] = useState<ReadonlyArray<MemberRow>>([]);
  const [error, setError] = useState<string | null>(null);

  const [inviteFormOpen, setInviteFormOpen] = useState(false);
  const [inviteEmail, setInviteEmailState] = useState('');
  const [invitePermissionRoleId, setInvitePermissionRoleIdState] = useState('');
  const [inviteFunctionRoleId, setInviteFunctionRoleIdState] = useState('');
  const [inviteStatus, setInviteStatus] = useState<InviteFormStatus>('idle');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [permissionRoleOptions, setPermissionRoleOptions] = useState<
    ReadonlyArray<PermissionRoleOption>
  >([]);
  const [functionRoleOptions, setFunctionRoleOptions] = useState<
    ReadonlyArray<FunctionRoleOption>
  >([]);
  const [optionsLoadedFor, setOptionsLoadedFor] = useState<string | null>(null);
  const [inviteSentTo, setInviteSentTo] = useState<string | null>(null);
  const [inviteEdgeFailed, setInviteEdgeFailed] = useState(false);
  const [inviteScrollToEmail, setInviteScrollToEmail] = useState<string | null>(
    null
  );

  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editPermissionRoleId, setEditPermissionRoleIdState] = useState('');
  const [editFunctionRoleId, setEditFunctionRoleIdState] = useState('');
  const [editStatus, setEditStatus] = useState<EditRoleStatus>('idle');
  const [editError, setEditError] = useState<string | null>(null);
  const [editSavedFor, setEditSavedFor] = useState<string | null>(null);
  const [editOriginalPermissionRoleId, setEditOriginalPermissionRoleId] =
    useState('');
  const [editOriginalFunctionRoleId, setEditOriginalFunctionRoleId] =
    useState('');

  const [promoteConfirmTargetUserId, setPromoteConfirmTargetUserId] = useState<
    string | null
  >(null);
  const [promoteConfirmStatus, setPromoteConfirmStatus] =
    useState<PromoteConfirmStatus>('idle');
  const [promoteConfirmError, setPromoteConfirmError] = useState<string | null>(
    null
  );
  const [promoteSavedFor, setPromoteSavedFor] = useState<string | null>(null);
  const [promoteConfirmName, setPromoteConfirmName] = useState<string | null>(
    null
  );

  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTargetUserId, setTransferTargetUserIdState] = useState('');
  const [transferStatus, setTransferStatus] =
    useState<TransferModalStatus>('idle');
  const [transferError, setTransferError] = useState<string | null>(null);
  const [transferSavedName, setTransferSavedName] = useState<string | null>(
    null
  );

  const [removeTargetUserId, setRemoveTargetUserId] = useState<string | null>(
    null
  );
  const [removeTargetName, setRemoveTargetName] = useState<string | null>(null);
  const [removeStatus, setRemoveStatus] = useState<RemoveStatus>('idle');
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [removeSavedName, setRemoveSavedName] = useState<string | null>(null);

  // Resend state (turn 2c.3). Keyed by invitation_id so multiple Pending rows
  // can track their own cooldown independently.
  const [resendingInvitationId, setResendingInvitationId] = useState<
    string | null
  >(null);
  const [resendCooldownById, setResendCooldownById] = useState<
    Record<string, number>
  >({});
  const [resendErrorById, setResendErrorById] = useState<Record<string, string>>(
    {}
  );
  const [resendSavedEmail, setResendSavedEmail] = useState<string | null>(null);
  const [resendEdgeFailed, setResendEdgeFailed] = useState(false);
  const [resendNow, setResendNow] = useState(() => Date.now());

  // Ticker — runs only while at least one cooldown is still in the future.
  useEffect(() => {
    const anyActive = Object.values(resendCooldownById).some(
      (t) => t > Date.now()
    );
    if (!anyActive) return;
    const id = setInterval(() => setResendNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, [resendCooldownById]);

  function normalizeRpcRows(raw: ReadonlyArray<RawRow>): MemberRow[] {
    const out: MemberRow[] = [];
    for (const r of raw) {
      const kind = normalizeKind(r.kind);
      if (!kind) continue;
      out.push({
        kind,
        user_id: r.user_id ?? null,
        invitation_id: r.invitation_id ?? null,
        display_name: r.display_name ?? null,
        username: r.username ?? null,
        discriminator:
          typeof r.discriminator === 'number' ? r.discriminator : null,
        email: r.email ?? null,
        permission_role_system_key: r.permission_role_system_key ?? null,
        permission_role_name: r.permission_role_name ?? null,
        function_role_label: r.function_role_label ?? null,
        last_active_at: r.last_active_at ?? null,
        invited_at: r.invited_at ?? null,
        expires_at: r.expires_at ?? null,
        is_self: r.is_self === true
      });
    }
    return out;
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;
      const session = sessionData.session;
      if (!session) {
        setStatus('unauth');
        return;
      }

      // No active org yet — the shell is still resolving which org to land in.
      // Stay in the loading state; this effect re-runs once it resolves.
      if (!activeOrgId) return;

      const { data: rpcRows, error: rpcErr } = await supabase.rpc(
        'list_org_members',
        { p_org_id: activeOrgId }
      );
      if (cancelled) return;
      if (rpcErr) {
        setStatus('error');
        setError("Couldn't load members. Please try again.");
        return;
      }

      setRows(normalizeRpcRows((rpcRows ?? []) as ReadonlyArray<RawRow>));
      setStatus('ready');
    })();
    return () => {
      cancelled = true;
    };
  }, [activeOrgId]);

  const canSeeEmails = rows.some((r) => !!r.email);
  const activeCount = rows.filter((r) => r.kind === 'active').length;
  const pendingCount = rows.filter((r) => r.kind === 'pending').length;
  const expiredCount = rows.filter((r) => r.kind === 'expired').length;

  const viewerPermissionSystemKey = ((): ViewerPermissionSystemKey => {
    const selfRow = rows.find((r) => r.is_self && r.kind === 'active');
    const k = selfRow?.permission_role_system_key;
    if (k === 'owner' || k === 'admin' || k === 'member') return k;
    return null;
  })();

  const capabilities: MemberCapabilities = {
    canInvite:
      viewerPermissionSystemKey === 'owner' ||
      viewerPermissionSystemKey === 'admin',
    canTransfer: viewerPermissionSystemKey === 'owner',
    canEditRole:
      viewerPermissionSystemKey === 'owner' ||
      viewerPermissionSystemKey === 'admin',
    canRemove:
      viewerPermissionSystemKey === 'owner' ||
      viewerPermissionSystemKey === 'admin'
  };

  function rowActions(row: MemberRow): RowActionVisibility {
    if (row.kind === 'active') {
      if (row.is_self) {
        return { edit: false, remove: false, resend: false, reinvite: false, revoke: false };
      }
      const isOwnerRow = row.permission_role_system_key === 'owner';
      const isAdminRow = row.permission_role_system_key === 'admin';
      // Admins cannot edit or remove other Admins or Owners.
      const blockedForAdmin =
        viewerPermissionSystemKey === 'admin' && (isOwnerRow || isAdminRow);
      // Even an Owner viewer cannot direct-edit an Owner row — that path is
      // Transfer ownership only.
      const blockedOwnerDirectEdit =
        viewerPermissionSystemKey === 'owner' && isOwnerRow;
      return {
        edit: capabilities.canEditRole && !blockedForAdmin && !blockedOwnerDirectEdit,
        remove: capabilities.canRemove && !blockedForAdmin && !isOwnerRow,
        resend: false,
        reinvite: false,
        revoke: false
      };
    }
    if (row.kind === 'pending') {
      return {
        edit: false,
        remove: false,
        resend: capabilities.canInvite,
        reinvite: false,
        revoke: capabilities.canInvite
      };
    }
    // expired
    return {
      edit: false,
      remove: false,
      resend: false,
      reinvite: capabilities.canInvite,
      revoke: capabilities.canInvite
    };
  }

  const invitePermissionRoleOptions = permissionRoleOptions.filter(
    (p) => p.system_key !== 'owner'
  );
  const editPermissionRoleOptions =
    viewerPermissionSystemKey === 'owner'
      ? permissionRoleOptions
      : permissionRoleOptions.filter((p) => p.system_key !== 'owner');

  const inviteEmailTrimmed = inviteEmail.trim();
  const inviteEmailValid = EMAIL_PATTERN.test(inviteEmailTrimmed);
  const inviteFormCanSubmit =
    inviteFormOpen &&
    inviteEmailValid &&
    !!invitePermissionRoleId &&
    !!inviteFunctionRoleId;

  const openInviteForm = useCallback(() => {
    setInviteFormOpen(true);
    setInviteError(null);
    if (!activeOrgId) return;
    if (optionsLoadedFor === activeOrgId) {
      setInviteStatus('ready');
      return;
    }
    setInviteStatus('loading-options');
    (async () => {
      const [permRes, funcRes] = await Promise.all([
        supabase
          .from('permission_groups')
          .select('id, name, system_key')
          .eq('org_id', activeOrgId),
        supabase
          .from('function_roles')
          .select('id, key, label')
          .eq('org_id', activeOrgId)
      ]);
      if (permRes.error || funcRes.error) {
        setInviteStatus('options-error');
        setInviteError("Couldn't load invite options. Please try again.");
        return;
      }
      const perms = (permRes.data ?? []) as PermissionRoleOption[];
      const funcs = (funcRes.data ?? []) as FunctionRoleOption[];
      setPermissionRoleOptions(perms);
      setFunctionRoleOptions(funcs);
      setOptionsLoadedFor(activeOrgId);
      setInviteStatus('ready');
    })();
  }, [activeOrgId, optionsLoadedFor]);

  const cancelInviteForm = useCallback(() => {
    setInviteFormOpen(false);
    setInviteEmailState('');
    setInvitePermissionRoleIdState('');
    setInviteFunctionRoleIdState('');
    setInviteError(null);
    setInviteStatus('idle');
    setInviteScrollToEmail(null);
  }, []);

  const setInviteEmail = useCallback((v: string) => {
    setInviteEmailState(v);
    setInviteError(null);
  }, []);

  const setInvitePermissionRoleId = useCallback((v: string) => {
    setInvitePermissionRoleIdState(v);
  }, []);

  const setInviteFunctionRoleId = useCallback((v: string) => {
    setInviteFunctionRoleIdState(v);
  }, []);

  const submitInvite = useCallback(async () => {
    if (!activeOrgId) return;
    const email = inviteEmailTrimmed.toLowerCase();
    if (!EMAIL_PATTERN.test(email)) return;
    if (!invitePermissionRoleId || !inviteFunctionRoleId) return;

    setInviteStatus('submitting');
    setInviteError(null);
    setInviteScrollToEmail(null);
    setInviteEdgeFailed(false);

    const { data, error } = await supabase.rpc('create_invitation', {
      p_org_id: activeOrgId,
      p_email: email,
      p_permission_role_id: invitePermissionRoleId,
      p_function_role_id: inviteFunctionRoleId
    });

    if (error) {
      setInviteStatus('error');
      setInviteError("Couldn't send the invitation. Please try again.");
      return;
    }

    const result = data as
      | { ok?: boolean; error?: string; token?: string }
      | null;

    if (!result || result.ok !== true) {
      if (result?.error === 'already_member') {
        setInviteStatus('error');
        setInviteError(
          `${email} is already a member of ${activeOrgName ?? 'this org'}.`
        );
        return;
      }
      if (result?.error === 'still_pending') {
        setInviteStatus('error');
        setInviteError(
          `There's already a pending invitation for ${email}. Resend or Revoke from the list.`
        );
        setInviteScrollToEmail(email);
        return;
      }
      setInviteStatus('error');
      setInviteError("Couldn't send the invitation. Please try again.");
      return;
    }

    let edgeOk = true;
    try {
      const fnRes = await supabase.functions.invoke('send-invitation-email', {
        body: { token: result.token }
      });
      if (fnRes.error) edgeOk = false;
      else {
        const fnData = fnRes.data as { ok?: boolean } | null;
        if (!fnData || fnData.ok !== true) edgeOk = false;
      }
    } catch {
      edgeOk = false;
    }

    const { data: rpcRows, error: rpcErr } = await supabase.rpc(
      'list_org_members',
      { p_org_id: activeOrgId }
    );
    if (!rpcErr) {
      setRows(normalizeRpcRows((rpcRows ?? []) as ReadonlyArray<RawRow>));
    }

    setInviteStatus('success');
    setInviteSentTo(email);
    setInviteEdgeFailed(!edgeOk);
    setInviteFormOpen(false);
    setInviteEmailState('');
    setInvitePermissionRoleIdState('');
    setInviteFunctionRoleIdState('');
    // Cluster 5G: pending invitations consume a seat but don't fire the
    // realtime `memberships` subscription that `useSeatUsage` watches, so the
    // seat-counter pill + Invite-button gate would stay stale until the next
    // visibilitychange. The page wires this callback to `seats.refetch()`.
    onInviteSuccessRef.current?.();
  }, [
    activeOrgId,
    activeOrgName,
    inviteEmailTrimmed,
    invitePermissionRoleId,
    inviteFunctionRoleId
  ]);

  const clearInviteToast = useCallback(() => {
    setInviteSentTo(null);
    setInviteEdgeFailed(false);
    if (inviteStatus === 'success') setInviteStatus('idle');
  }, [inviteStatus]);

  const clearInviteScrollTarget = useCallback(() => {
    setInviteScrollToEmail(null);
  }, []);

  const startEditRole = useCallback(
    (row: MemberRow) => {
      if (!row.user_id) return;
      if (row.is_self || row.kind !== 'active') return;
      if (row.permission_role_system_key === 'owner') return;
      if (!activeOrgId) return;

      setEditError(null);
      setEditSavedFor(null);

      const matchAndApply = (
        perms: ReadonlyArray<PermissionRoleOption>,
        funcs: ReadonlyArray<FunctionRoleOption>
      ) => {
        const permId =
          perms.find(
            (p) => p.system_key === row.permission_role_system_key
          )?.id ?? '';
        const funcId =
          funcs.find((f) => f.label === row.function_role_label)?.id ?? '';
        setEditPermissionRoleIdState(permId);
        setEditFunctionRoleIdState(funcId);
        setEditOriginalPermissionRoleId(permId);
        setEditOriginalFunctionRoleId(funcId);
      };

      setEditingUserId(row.user_id);

      if (optionsLoadedFor === activeOrgId) {
        matchAndApply(permissionRoleOptions, functionRoleOptions);
        setEditStatus('editing');
        return;
      }

      setEditStatus('loading-options');
      (async () => {
        const [permRes, funcRes] = await Promise.all([
          supabase
            .from('permission_groups')
            .select('id, name, system_key')
            .eq('org_id', activeOrgId),
          supabase
            .from('function_roles')
            .select('id, key, label')
            .eq('org_id', activeOrgId)
        ]);
        if (permRes.error || funcRes.error) {
          setEditStatus('options-error');
          setEditError("Couldn't load role options. Please try again.");
          return;
        }
        const perms = (permRes.data ?? []) as PermissionRoleOption[];
        const funcs = (funcRes.data ?? []) as FunctionRoleOption[];
        setPermissionRoleOptions(perms);
        setFunctionRoleOptions(funcs);
        setOptionsLoadedFor(activeOrgId);
        matchAndApply(perms, funcs);
        setEditStatus('editing');
      })();
    },
    [activeOrgId, optionsLoadedFor, permissionRoleOptions, functionRoleOptions]
  );

  const cancelEditRole = useCallback(() => {
    setEditingUserId(null);
    setEditPermissionRoleIdState('');
    setEditFunctionRoleIdState('');
    setEditOriginalPermissionRoleId('');
    setEditOriginalFunctionRoleId('');
    setEditStatus('idle');
    setEditError(null);
  }, []);

  const setEditPermissionRoleId = useCallback((v: string) => {
    setEditPermissionRoleIdState(v);
    setEditError(null);
  }, []);

  const setEditFunctionRoleId = useCallback((v: string) => {
    setEditFunctionRoleIdState(v);
    setEditError(null);
  }, []);

  const requestPromoteToOwner = useCallback(
    (targetUserId: string) => {
      const targetRow = rows.find(
        (r) => r.user_id === targetUserId && r.kind === 'active'
      );
      const displayName =
        targetRow?.display_name ?? targetRow?.email ?? 'member';
      setPromoteConfirmTargetUserId(targetUserId);
      setPromoteConfirmName(displayName);
      setPromoteConfirmStatus('idle');
      setPromoteConfirmError(null);
    },
    [rows]
  );

  const saveRoleChange = useCallback(async () => {
    if (!editingUserId || !activeOrgId) return;
    if (editStatus !== 'editing' && editStatus !== 'error') return;

    const permChanged =
      !!editPermissionRoleId &&
      editPermissionRoleId !== editOriginalPermissionRoleId;
    const funcChanged =
      !!editFunctionRoleId &&
      editFunctionRoleId !== editOriginalFunctionRoleId;

    // Route Owner promotions through the confirm Modal + transfer_ownership
    // RPC instead of update_member_permission_role.
    if (permChanged) {
      const chosen = permissionRoleOptions.find(
        (p) => p.id === editPermissionRoleId
      );
      if (chosen?.system_key === 'owner') {
        requestPromoteToOwner(editingUserId);
        return;
      }
    }

    if (!permChanged && !funcChanged) {
      // No-op — close the editor without firing any RPC.
      setEditingUserId(null);
      setEditPermissionRoleIdState('');
      setEditFunctionRoleIdState('');
      setEditOriginalPermissionRoleId('');
      setEditOriginalFunctionRoleId('');
      setEditStatus('idle');
      return;
    }

    setEditStatus('saving');
    setEditError(null);

    type RpcResult = { data: unknown; error: { message?: string } | null };
    const calls: Promise<RpcResult>[] = [];
    if (permChanged) {
      calls.push(
        supabase.rpc('update_member_permission_role', {
          p_org_id: activeOrgId,
          p_target_user_id: editingUserId,
          p_permission_role_id: editPermissionRoleId
        }) as unknown as Promise<RpcResult>
      );
    }
    if (funcChanged) {
      calls.push(
        supabase.rpc('update_member_function_role', {
          p_org_id: activeOrgId,
          p_target_user_id: editingUserId,
          p_function_role_id: editFunctionRoleId
        }) as unknown as Promise<RpcResult>
      );
    }

    const results = await Promise.all(calls);
    const failure = results.find((r) => {
      if (r.error) return true;
      const payload = r.data as { ok?: boolean } | null;
      return !payload || payload.ok !== true;
    });
    if (failure) {
      setEditStatus('error');
      setEditError("Couldn't save the role change. Please try again.");
      return;
    }

    const { data: rpcRows, error: rpcErr } = await supabase.rpc(
      'list_org_members',
      { p_org_id: activeOrgId }
    );
    if (!rpcErr) {
      setRows(normalizeRpcRows((rpcRows ?? []) as ReadonlyArray<RawRow>));
    }

    const savedUserId = editingUserId;
    setEditingUserId(null);
    setEditPermissionRoleIdState('');
    setEditFunctionRoleIdState('');
    setEditOriginalPermissionRoleId('');
    setEditOriginalFunctionRoleId('');
    setEditStatus('idle');
    setEditSavedFor(savedUserId);
  }, [
    activeOrgId,
    editingUserId,
    editStatus,
    editPermissionRoleId,
    editFunctionRoleId,
    editOriginalPermissionRoleId,
    editOriginalFunctionRoleId,
    permissionRoleOptions,
    requestPromoteToOwner
  ]);

  const clearEditToast = useCallback(() => {
    setEditSavedFor(null);
  }, []);

  const cancelPromoteToOwner = useCallback(() => {
    setPromoteConfirmTargetUserId(null);
    setPromoteConfirmName(null);
    setPromoteConfirmStatus('idle');
    setPromoteConfirmError(null);
  }, []);

  const confirmPromoteToOwner = useCallback(async () => {
    if (!promoteConfirmTargetUserId || !activeOrgId) return;
    setPromoteConfirmStatus('submitting');
    setPromoteConfirmError(null);

    const targetUserId = promoteConfirmTargetUserId;

    const { data, error } = await supabase.rpc('transfer_ownership', {
      p_org_id: activeOrgId,
      p_target_user_id: targetUserId
    });

    if (error) {
      setPromoteConfirmStatus('error');
      setPromoteConfirmError(
        "Couldn't promote to Owner. Please try again."
      );
      return;
    }

    const result = data as { ok?: boolean } | null;
    if (!result || result.ok !== true) {
      setPromoteConfirmStatus('error');
      setPromoteConfirmError(
        "Couldn't promote to Owner. Please try again."
      );
      return;
    }

    const { data: rpcRows, error: rpcErr } = await supabase.rpc(
      'list_org_members',
      { p_org_id: activeOrgId }
    );
    if (!rpcErr) {
      setRows(normalizeRpcRows((rpcRows ?? []) as ReadonlyArray<RawRow>));
    }

    // Exit edit mode + clear all edit state.
    setEditingUserId(null);
    setEditPermissionRoleIdState('');
    setEditFunctionRoleIdState('');
    setEditOriginalPermissionRoleId('');
    setEditOriginalFunctionRoleId('');
    setEditStatus('idle');
    setEditError(null);

    // Close Modal + surface toast.
    setPromoteConfirmTargetUserId(null);
    setPromoteConfirmStatus('idle');
    setPromoteConfirmError(null);
    setPromoteSavedFor(targetUserId);
  }, [activeOrgId, promoteConfirmTargetUserId]);

  const clearPromoteToast = useCallback(() => {
    setPromoteSavedFor(null);
    setPromoteConfirmName(null);
  }, []);

  const transferTargetOptions: TransferTargetOption[] = rows
    .filter(
      (r) =>
        r.kind === 'active' &&
        !r.is_self &&
        r.permission_role_system_key !== 'owner' &&
        !!r.user_id
    )
    .map((r) => {
      const name = r.display_name ?? r.email ?? 'member';
      const handle = r.username
        ? ` @${r.username}#${String(r.discriminator ?? 0)
            .padStart(4, '0')
            .slice(-4)}`
        : '';
      return {
        userId: r.user_id!,
        label: `${name}${handle}`,
        displayName: name
      };
    });

  const openTransferModal = useCallback(() => {
    setTransferOpen(true);
    setTransferTargetUserIdState('');
    setTransferStatus('idle');
    setTransferError(null);
  }, []);

  const cancelTransferModal = useCallback(() => {
    setTransferOpen(false);
    setTransferTargetUserIdState('');
    setTransferStatus('idle');
    setTransferError(null);
  }, []);

  const setTransferTargetUserId = useCallback((v: string) => {
    setTransferTargetUserIdState(v);
    setTransferError(null);
  }, []);

  const confirmTransfer = useCallback(async () => {
    if (!activeOrgId || !transferTargetUserId) return;
    const targetRow = rows.find(
      (r) => r.user_id === transferTargetUserId && r.kind === 'active'
    );
    const displayName =
      targetRow?.display_name ?? targetRow?.email ?? 'member';

    setTransferStatus('submitting');
    setTransferError(null);

    const { data, error } = await supabase.rpc('transfer_ownership', {
      p_org_id: activeOrgId,
      p_target_user_id: transferTargetUserId
    });

    if (error) {
      setTransferStatus('error');
      setTransferError("Couldn't transfer ownership. Please try again.");
      return;
    }

    const result = data as { ok?: boolean } | null;
    if (!result || result.ok !== true) {
      setTransferStatus('error');
      setTransferError("Couldn't transfer ownership. Please try again.");
      return;
    }

    const { data: rpcRows, error: rpcErr } = await supabase.rpc(
      'list_org_members',
      { p_org_id: activeOrgId }
    );
    if (!rpcErr) {
      setRows(normalizeRpcRows((rpcRows ?? []) as ReadonlyArray<RawRow>));
    }

    setTransferOpen(false);
    setTransferTargetUserIdState('');
    setTransferStatus('idle');
    setTransferError(null);
    setTransferSavedName(displayName);
  }, [activeOrgId, transferTargetUserId, rows]);

  const clearTransferToast = useCallback(() => {
    setTransferSavedName(null);
  }, []);

  const requestRemove = useCallback((row: MemberRow) => {
    if (!row.user_id || row.kind !== 'active' || row.is_self) return;
    const name = row.display_name ?? row.email ?? 'member';
    setRemoveTargetUserId(row.user_id);
    setRemoveTargetName(name);
    setRemoveStatus('idle');
    setRemoveError(null);
  }, []);

  const cancelRemove = useCallback(() => {
    setRemoveTargetUserId(null);
    setRemoveTargetName(null);
    setRemoveStatus('idle');
    setRemoveError(null);
  }, []);

  const confirmRemove = useCallback(async () => {
    if (!removeTargetUserId || !activeOrgId) return;
    const targetName = removeTargetName ?? 'member';
    setRemoveStatus('submitting');
    setRemoveError(null);

    const { data, error } = await supabase.rpc('remove_member', {
      p_org_id: activeOrgId,
      p_target_user_id: removeTargetUserId
    });

    if (error) {
      setRemoveStatus('error');
      setRemoveError("Couldn't remove member. Please try again.");
      return;
    }
    const result = data as { ok?: boolean } | null;
    if (!result || result.ok !== true) {
      setRemoveStatus('error');
      setRemoveError("Couldn't remove member. Please try again.");
      return;
    }

    const { data: rpcRows, error: rpcErr } = await supabase.rpc(
      'list_org_members',
      { p_org_id: activeOrgId }
    );
    if (!rpcErr) {
      setRows(normalizeRpcRows((rpcRows ?? []) as ReadonlyArray<RawRow>));
    }

    setRemoveTargetUserId(null);
    setRemoveTargetName(null);
    setRemoveStatus('idle');
    setRemoveError(null);
    setRemoveSavedName(targetName);
  }, [activeOrgId, removeTargetUserId, removeTargetName]);

  const clearRemoveToast = useCallback(() => {
    setRemoveSavedName(null);
  }, []);

  const resendInvitation = useCallback(
    async (row: MemberRow) => {
      const invId = row.invitation_id;
      if (!invId) return;
      if ((resendCooldownById[invId] ?? 0) > Date.now()) return;
      if (resendingInvitationId !== null) return;

      setResendingInvitationId(invId);
      setResendErrorById((m) => {
        if (!(invId in m)) return m;
        const n = { ...m };
        delete n[invId];
        return n;
      });

      const { data: invRow, error: tokErr } = await supabase
        .from('invitations')
        .select('token')
        .eq('id', invId)
        .maybeSingle();
      const token = (invRow as { token?: string } | null)?.token ?? null;
      if (tokErr || !token) {
        setResendingInvitationId(null);
        setResendErrorById((m) => ({
          ...m,
          [invId]: "Couldn't resend the invitation. Please try again."
        }));
        return;
      }

      const { data, error } = await supabase.rpc('resend_invitation', {
        p_token: token
      });

      if (error) {
        // PostgREST surfaces our P0001 `detail = jsonb_build_object(...)` as a
        // JSON-encoded string on `error.details`. Parse `seconds_remaining`
        // out of it to drive the per-row cooldown countdown.
        let seconds: number | null = null;
        const raw = (error as { details?: unknown }).details;
        try {
          const parsed =
            typeof raw === 'string'
              ? JSON.parse(raw)
              : (raw as Record<string, unknown> | null);
          if (
            parsed &&
            typeof parsed === 'object' &&
            (parsed as Record<string, unknown>).reason === 'cooldown' &&
            typeof (parsed as Record<string, unknown>).seconds_remaining ===
              'number'
          ) {
            seconds = (parsed as { seconds_remaining: number })
              .seconds_remaining;
          }
        } catch {
          /* not JSON — fall through to generic */
        }
        setResendingInvitationId(null);
        if (seconds !== null) {
          setResendCooldownById((m) => ({
            ...m,
            [invId]: Date.now() + seconds! * 1_000
          }));
          setResendNow(Date.now());
          return;
        }
        const msg = /rate|too many/i.test(error.message ?? '')
          ? 'Too many requests. Try again in a few minutes.'
          : "Couldn't resend the invitation. Please try again.";
        setResendErrorById((m) => ({ ...m, [invId]: msg }));
        return;
      }

      const result = data as
        | { ok?: boolean; token?: string; email?: string }
        | null;
      if (!result || result.ok !== true) {
        setResendingInvitationId(null);
        setResendErrorById((m) => ({
          ...m,
          [invId]: "Couldn't resend the invitation. Please try again."
        }));
        return;
      }

      let edgeOk = true;
      try {
        const fnRes = await supabase.functions.invoke('send-invitation-email', {
          body: { token: result.token }
        });
        if (fnRes.error) edgeOk = false;
        else {
          const fnData = fnRes.data as { ok?: boolean } | null;
          if (!fnData || fnData.ok !== true) edgeOk = false;
        }
      } catch {
        edgeOk = false;
      }

      setResendingInvitationId(null);
      setResendCooldownById((m) => ({
        ...m,
        [invId]: Date.now() + 30_000
      }));
      setResendNow(Date.now());
      setResendSavedEmail(result.email ?? row.email ?? null);
      setResendEdgeFailed(!edgeOk);
    },
    [resendCooldownById, resendingInvitationId]
  );

  const clearResendToast = useCallback(() => {
    setResendSavedEmail(null);
    setResendEdgeFailed(false);
  }, []);

  const resendStateFor = useCallback(
    (row: MemberRow): ResendRowState => {
      const invId = row.invitation_id;
      if (!invId) return { busy: false, cooldownSeconds: 0, error: null };
      const expiresAt = resendCooldownById[invId] ?? 0;
      const remaining = Math.max(
        0,
        Math.ceil((expiresAt - resendNow) / 1_000)
      );
      return {
        busy: resendingInvitationId === invId,
        cooldownSeconds: remaining,
        error: resendErrorById[invId] ?? null
      };
    },
    [resendCooldownById, resendErrorById, resendNow, resendingInvitationId]
  );

  return {
    status,
    activeOrgId,
    activeOrgName,
    rows,
    canSeeEmails,
    activeCount,
    pendingCount,
    expiredCount,
    viewerPermissionSystemKey,
    capabilities,
    rowActions,
    error,
    inviteFormOpen,
    inviteEmail,
    invitePermissionRoleId,
    inviteFunctionRoleId,
    inviteStatus,
    inviteError,
    permissionRoleOptions,
    invitePermissionRoleOptions,
    editPermissionRoleOptions,
    functionRoleOptions,
    inviteEmailValid,
    inviteFormCanSubmit,
    inviteSentTo,
    inviteEdgeFailed,
    inviteScrollToEmail,
    openInviteForm,
    cancelInviteForm,
    setInviteEmail,
    setInvitePermissionRoleId,
    setInviteFunctionRoleId,
    submitInvite,
    clearInviteToast,
    clearInviteScrollTarget,
    editingUserId,
    editPermissionRoleId,
    editFunctionRoleId,
    editStatus,
    editError,
    editSavedFor,
    startEditRole,
    cancelEditRole,
    setEditPermissionRoleId,
    setEditFunctionRoleId,
    saveRoleChange,
    clearEditToast,
    promoteConfirmTargetUserId,
    promoteConfirmStatus,
    promoteConfirmError,
    promoteSavedFor,
    promoteConfirmName,
    requestPromoteToOwner,
    cancelPromoteToOwner,
    confirmPromoteToOwner,
    clearPromoteToast,
    transferOpen,
    transferTargetUserId,
    transferStatus,
    transferError,
    transferSavedName,
    transferTargetOptions,
    openTransferModal,
    cancelTransferModal,
    setTransferTargetUserId,
    confirmTransfer,
    clearTransferToast,
    removeTargetUserId,
    removeTargetName,
    removeStatus,
    removeError,
    removeSavedName,
    requestRemove,
    cancelRemove,
    confirmRemove,
    clearRemoveToast,
    resendStateFor,
    resendSavedEmail,
    resendEdgeFailed,
    resendInvitation,
    clearResendToast
  };
}
